// useUrlOverlay — an overlay held in the link and its ONE history step (ADR-037
// §2; FRONTEND-PATTERNS §URL state). The peek (`?peek=`, through useEntityPeek),
// the file viewer (`?file=`) and the import run (`?job=`) all ride it, so the
// three follow one rule:
//   • open (none → a value)  → PUSH through the ROUTER (never nuqs — the router's
//     history index must count the entry; lib/historyEntry pops by it), the entry
//     marked as the overlay's step (lib/entryState: the query it opened over and
//     that place's scroll memory — the open never moves the page)
//   • a move to another record while open → REPLACE (one step, however many moves)
//   • close → works exactly like Back: the step is GIVEN BACK, and a change the
//     person made to the page while the overlay was open is carried down onto the
//     page's own entry as the browser lands — history ends as if the overlay had
//     never opened, Back is never left dead. A pasted link was never this page's
//     step: its close replaces.
//   • follow (a link INSIDE the overlay to another record) → REPLACE, the record
//     left joining the way back (lib/entryState — the entry's own state, beside
//     the mark); `back` (the chevron) replaces with the last of it. Still the one
//     step: the browser's Back closes the whole overlay, never walking record by
//     record (a face of one place, as a tab is). A fresh pick — an open while
//     open, a row of the list — starts the way back empty.
// Leaving the overlay for a page rides `useOverlayLeave`: an entity tab gives the
// step back first, then switches; Open full page uses the step up.
//
// A memory router (a story, a test) is no address: the open writes the address
// directly and the close replaces — there is no browser history to keep honest.

import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import { url, useUrlState } from './useUrlState.js';
import {
  carriedAddress,
  liveState,
  overlayStepOf,
  overlayTrailOf,
  placeUnchanged,
  pushEntry,
  queryOf,
  routerIsAddress,
  withOverlayStep,
  withOverlayTrail,
  withoutOverlayStep,
  writeEntry,
  type OverlayStep,
  type TrailRecord,
} from '../lib/entryState.js';

export interface UrlOverlay {
  /** The open overlay's value, or null when closed. */
  value: string | null;
  /** Open the overlay — its one step (a push); while it is open, a move to another
   *  record replaces. `clearing` names sibling params closed in the SAME move — the
   *  door out of a modal (a peek never outranks a modal): that always pushes, and
   *  Back — or the close — returns to the modal. */
  open: (value: string, clearing?: string[]) => void;
  /** Close it — the step given back, as Back does (the rule above). */
  close: () => void;
  /** Follow a link INSIDE the overlay to another record: the record left — `fromLabel`, the name its back chevron
   *  says — joins the way back, and the overlay moves (a replace: still its one step). */
  follow: (next: string, fromLabel: string) => void;
  /** The way back — the records followed through, oldest first; empty when the overlay was opened, not followed into. */
  trail: readonly TrailRecord[];
  /** Step back to the record the last link was followed from (a replace). */
  back: () => void;
}

/** The live entry's state with `param`'s way back taken off — a fresh pick starts it empty. */
function withoutTrail(param: string): unknown {
  return withOverlayTrail(liveState(), param, []);
}

function searchOf(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `?${query}` : '';
}

// The address a step back is leaving, until the browser lands — a second close in
// the meantime (a double click, a repeated Escape) must not step back twice.
let leaving: string | null = null;

/** Give the current entry back (a pop). `carry` — the address the entry below is to show (a change made while the
 *  overlay was open): written the moment the browser lands, before anything renders it. `then` runs once the router
 *  has settled. */
function stepBack(navigate: NavigateFunction, opts: { carry?: string; then?: () => void } = {}): void {
  if (leaving === window.location.href) return;
  leaving = window.location.href;
  window.addEventListener(
    'popstate',
    () => {
      leaving = null;
      if (opts.carry) writeEntry(liveState(), opts.carry);
      // The router handles the pop first (its listener came first); the next task sees it settled.
      if (opts.then) setTimeout(opts.then, 0);
    },
    { once: true },
  );
  void navigate(-1);
}

/** The address the step's close carries down — none when the page under it is as it was. */
function carryOf(step: OverlayStep): string | undefined {
  const { pathname, search } = window.location;
  return placeUnchanged(step, search) ? undefined : carriedAddress(step, pathname, search);
}

/** Move the open overlay to `next` in place, the entry's own state rewritten with it (the way back): through the
 *  router where it is the address — its location must carry the new state — else on the window (a memory router).
 *  The router alone — no URL-state store: the panel's chevron rides it too. */
function useOverlayRewrite(param: string): (next: string, entryState: unknown) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (next: string, entryState: unknown) => {
      const params = new URLSearchParams(window.location.search);
      params.set(param, next);
      if (!routerIsAddress(location.key)) {
        writeEntry(entryState, `${window.location.pathname}${searchOf(params)}`);
        return;
      }
      void navigate({ pathname: location.pathname, search: searchOf(params) }, { replace: true, preventScrollReset: true, state: entryState });
    },
    [param, navigate, location.pathname, location.key],
  );
}

export interface OverlayTrail {
  /** The way back — the records followed through, oldest first; empty when the overlay was opened, not followed into
   *  (or is closed). */
  trail: readonly TrailRecord[];
  /** Step back to the record the last link was followed from (a replace). */
  back: () => void;
}

/** The way back of the overlay held in `param`, read from the live entry (one write moves the value and the way back
 *  together, so a render that sees the one sees the other) — the router alone: the sheet's chevron reads it. */
export function useOverlayTrail(param: string): OverlayTrail {
  const rewrite = useOverlayRewrite(param);
  useLocation(); // a router move (follow · back · close) renders the reader again
  const open = new URLSearchParams(window.location.search).has(param);
  const trail = open ? overlayTrailOf(liveState(), param) : [];
  const back = useCallback(() => {
    const records = overlayTrailOf(liveState(), param);
    const last = records.at(-1);
    if (last) rewrite(last.value, withOverlayTrail(liveState(), param, records.slice(0, -1)));
  }, [param, rewrite]);
  return { trail, back };
}

export function useUrlOverlay(param: string): UrlOverlay {
  const keys = useMemo(() => ({ [param]: url.text() }), [param]);
  const [state, setState] = useUrlState(keys);
  const navigate = useNavigate();
  const location = useLocation();
  // Open while the query holds the param — and, under the browser router, while the router's own location still
  // carries it: a navigation that takes it away (Open full page, a link) then closes the overlay in the SAME render
  // as the page change, whichever of the two states renders first, so the sheet is cut, never caught sliding out.
  const carried = !routerIsAddress(location.key) || new URLSearchParams(location.search).has(param);
  const value = state[param] && carried ? state[param] : null;

  const rewrite = useOverlayRewrite(param);
  const { trail: shownTrail, back } = useOverlayTrail(param);

  const open = useCallback(
    (next: string, clearing?: string[]) => {
      const { pathname, search } = window.location;
      // Open already? The address says so the moment the step is pushed — a render may not have caught up yet (a
      // double click): a second open is a move, never a second step — a fresh pick, so the way back starts empty.
      if ((value || new URLSearchParams(search).get(param)) && !clearing?.length) {
        if (overlayTrailOf(liveState(), param).length) rewrite(next, withoutTrail(param));
        else void setState({ [param]: next }, { history: 'replace' });
        return;
      }
      const params = new URLSearchParams(search);
      const cleared = (clearing ?? []).filter((key) => params.has(key));
      for (const key of cleared) params.delete(key);
      params.set(param, next);
      // A memory router is no address: write the address as nuqs would, the router stays.
      if (!routerIsAddress(location.key)) {
        pushEntry(withoutTrail(param), `${pathname}${searchOf(params)}`);
        return;
      }
      // An overlay opened from inside another shares the scroll memory the outer one shares — while its place is unchanged.
      const outer = overlayStepOf(location.state);
      const scroll = outer && placeUnchanged(outer, search) ? outer.scroll : location.key;
      void navigate(
        { pathname: location.pathname, search: searchOf(params) },
        { state: withOverlayStep({ param, base: queryOf(search), cleared, scroll }) },
      );
    },
    [value, setState, param, navigate, location.pathname, location.state, location.key, rewrite],
  );

  const close = useCallback(() => {
    if (!routerIsAddress(location.key)) {
      // The way back stays with the entry the close leaves (here, in place — a memory router keeps no other).
      if (overlayTrailOf(liveState(), param).length) writeEntry(withoutTrail(param));
      void setState({ [param]: null }, { history: 'replace' });
      return;
    }
    const step = overlayStepOf(liveState());
    if (step?.param === param) {
      stepBack(navigate, { carry: carryOf(step) });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.delete(param);
    void navigate(
      { pathname: location.pathname, search: searchOf(params) },
      { replace: true, preventScrollReset: true, state: withoutOverlayStep(liveState(), param) },
    );
  }, [setState, param, navigate, location.pathname, location.key]);

  const follow = useCallback(
    (next: string, fromLabel: string) => {
      if (!value || next === value) return;
      const records = overlayTrailOf(liveState(), param);
      rewrite(next, withOverlayTrail(liveState(), param, [...records, { value, label: fromLabel }]));
    },
    [value, param, rewrite],
  );

  const trail = value ? shownTrail : [];

  return { value, open, close, follow, trail, back };
}

export interface OverlayLeave {
  /** A click on an entity tab. When an overlay's step holds the entry, it is given
   *  back first, then the tab replaces (the link's own move is taken over) — a tab
   *  never adds history. Otherwise the link moves as it does. */
  onTabClick: (e: ReactMouseEvent, to: string) => void;
  /** A click on Open full page. When the page under the overlay changed while it
   *  was open, the step is given back carrying the change, then the page is pushed
   *  (the link's own move is taken over) — Back returns to the page as the person
   *  left it. Otherwise the link's own replace uses the step up. */
  onCommitClick: (e: ReactMouseEvent, to: string) => void;
}

/** A click that moves this tab — not a new tab or window (modifier keys, another button), nor one already handled. */
function movesThisTab(e: ReactMouseEvent): boolean {
  return !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/** Leaving whatever overlay holds the current entry for a page — read from the entry's mark. */
export function useOverlayLeave(): OverlayLeave {
  const navigate = useNavigate();
  const location = useLocation();

  const onTabClick = useCallback(
    (e: ReactMouseEvent, to: string) => {
      if (!movesThisTab(e) || !overlayStepOf(liveState()) || !routerIsAddress(location.key)) return;
      e.preventDefault();
      stepBack(navigate, { then: () => void navigate(to, { replace: true }) });
    },
    [navigate, location.key],
  );

  const onCommitClick = useCallback(
    (e: ReactMouseEvent, to: string) => {
      const step = overlayStepOf(liveState());
      const carry = step ? carryOf(step) : undefined;
      if (!movesThisTab(e) || !carry || !routerIsAddress(location.key)) return;
      e.preventDefault();
      stepBack(navigate, { carry, then: () => void navigate(to) });
    },
    [navigate, location.key],
  );

  return { onTabClick, onCommitClick };
}
